import { ArrowUpRight, Sparkles, Tag } from "lucide-react";
import { motion } from "framer-motion";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import type { GuideProductCard } from "@/lib/guides";

type Props = {
  products: GuideProductCard[];
  sticky?: boolean;
};

export default function ProductsUsedCard({ products, sticky = false }: Props) {
  const theme = usePalette();
  if (!products.length) return null;

  return (
    <aside
      className={sticky ? "lg:sticky lg:top-24" : ""}
      style={{ fontFamily: fonts.body }}
    >
      <div
        className="rounded-3xl p-5"
        style={{
          backgroundColor: colors.cream,
          border: `1px solid ${colors.tan}33`,
          boxShadow: `0 20px 50px -30px ${colors.navy}40`,
        }}
      >
        <div className="mb-4 flex items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{ backgroundColor: `${theme.accent}1a`, color: theme.accent }}
          >
            <Sparkles size={16} />
          </div>
          <p
            className="text-xs uppercase"
            style={{ fontWeight: 800, letterSpacing: "0.18em", color: colors.navy }}
          >
            Products used
          </p>
        </div>
        <ul className="flex flex-col gap-3">
          {products.map((p, i) => (
            <motion.li
              key={p.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
            >
              <a
                href={p.href}
                target={p.openInNewTab ? "_blank" : undefined}
                rel={p.openInNewTab ? "noreferrer" : undefined}
                className="group flex items-start gap-3 rounded-2xl p-3 transition-all hover:-translate-y-0.5"
                style={{
                  backgroundColor: "white",
                  border: `1px solid ${colors.tan}1f`,
                }}
              >
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                  style={{
                    backgroundColor: `${theme.accentSoft}`,
                    border: `1px solid ${colors.tan}1f`,
                  }}
                >
                  {p.imageUrl ? (
                    <img
                      src={p.imageUrl}
                      alt={p.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <Tag size={18} style={{ color: theme.accent }} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {p.brand && (
                        <p
                          className="text-[10px] uppercase tracking-[0.14em]"
                          style={{ fontWeight: 700, color: `${colors.navy}77` }}
                        >
                          {p.brand}
                        </p>
                      )}
                      <p
                        className="truncate text-sm"
                        style={{ fontWeight: 700, color: colors.navy }}
                      >
                        {p.title}
                      </p>
                    </div>
                    <ArrowUpRight
                      size={16}
                      className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      style={{ color: theme.accent }}
                    />
                  </div>
                  {p.note && (
                    <p
                      className="mt-1 text-xs"
                      style={{ color: `${colors.navy}99`, letterSpacing: "0.02em" }}
                    >
                      {p.note}
                    </p>
                  )}
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {p.priceLabel && (
                      <span
                        className="text-xs"
                        style={{
                          color: colors.navy,
                          fontWeight: 700,
                          letterSpacing: "0.02em",
                        }}
                      >
                        {p.priceLabel}
                      </span>
                    )}
                    {p.discountCode && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em]"
                        style={{
                          backgroundColor: `${theme.warm}1f`,
                          color: colors.tanDeep,
                          fontWeight: 700,
                        }}
                      >
                        Code {p.discountCode}
                      </span>
                    )}
                    {p.role === "recommended" && (
                      <span
                        className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em]"
                        style={{
                          backgroundColor: `${colors.denimBlue}25`,
                          color: colors.navy,
                          fontWeight: 700,
                        }}
                      >
                        Also love
                      </span>
                    )}
                  </div>
                </div>
              </a>
            </motion.li>
          ))}
        </ul>
      </div>
    </aside>
  );
}
